import React, { ReactElement, useEffect, useMemo, useState } from "react"
import { Button, Header, Icon, Loader, Modal, Popup, Input } from "semantic-ui-react"
import Select from "components/Form/Select"

import GLSLogo from "assets/carriers/GLS.png"
import DHLLogo from "assets/carriers/DHL.png"
import RelaisColisLogo from "assets/carriers/RelaisColis.png"
import GeodisLogoSolo from "assets/carriers/logo-solo/Geodis.png"
import ColissionLogoSolo from "assets/carriers/logo-solo/Colissimo.png"
import UPSLogo from "assets/carriers/UPS.png"
import GeevLogo from "assets/carriers/Geev.png"
import PoppinsLogo from "assets/carriers/Poppins.png"
import { PopupMessage } from "components/Keeper/Keeper.styled"
import {
  ButtonContainer,
  CarrierLogo,
  CheckIconContainer,
  CheckIcon,
  CloseIconContainer,
  CloseIcon,
  LoaderIconContainer,
  DisablingLabel,
  DisablingSectionsContainer,
  DisablingSection,
  TooltipContent,
  TooltipTitle,
  TooltipField,
  TooltipAction,
} from "components/Keeper/AllowedCarriers.styled"
import {
  CarriersName,
  AllowedCarrierProps,
  KeeperCarrierButtonProps,
  KeeperCarrier,
  DisablingRequestedBy,
  DisablingReason,
} from "types/carrier.types"

const REQUESTER_LABELS: Record<DisablingRequestedBy, string> = {
  [DisablingRequestedBy.KEEPER]: "Keeper",
  [DisablingRequestedBy.CARRIER]: "Transporteur",
  [DisablingRequestedBy.PICKME_OPS]: "L’equipe Ops",
  [DisablingRequestedBy.PICKME_CARE]: "L’equipe Care",
}

const REASON_LABELS: Record<DisablingReason, string> = {
  [DisablingReason.TEMPORARY_UNAVAILABILITY]: "Indisponibilité temporaire du Keeper",
  [DisablingReason.ACTIVITY_STOP]: "Arrêt d’activité du Keeper",

  [DisablingReason.KEEPER_UNREACHABLE]: "Keeper injoignable",
  [DisablingReason.SCHEDULE_NON_COMPLIANCE]: "Non-respect de l’agenda",
  [DisablingReason.BEHAVIOR_ISSUE]: "Comportement du Keeper",
  [DisablingReason.ROUTE_INCOMPATIBILITY]: "Incompatibilité de tournée",
  [DisablingReason.ADDRESS_INVALID]: "Adresse / coordonnées non conformes",

  [DisablingReason.INTERNAL_INVESTIGATION]: "Suspension suite à enquête",
  [DisablingReason.POLICY_VIOLATION]: "Non-respect des règles Pickme",

  [DisablingReason.OTHER]: "Autre",
}

const REASONS_FOR_KEEPER = [
  DisablingReason.TEMPORARY_UNAVAILABILITY,
  DisablingReason.ACTIVITY_STOP,
  DisablingReason.OTHER,
]

const REASONS_FOR_CARRIER = [
  DisablingReason.KEEPER_UNREACHABLE,
  DisablingReason.SCHEDULE_NON_COMPLIANCE,
  DisablingReason.BEHAVIOR_ISSUE,
  DisablingReason.ROUTE_INCOMPATIBILITY,
  DisablingReason.ADDRESS_INVALID,
  DisablingReason.OTHER,
]

const REASONS_FOR_OPS_CARE = [
  DisablingReason.INTERNAL_INVESTIGATION,
  DisablingReason.POLICY_VIOLATION,
  DisablingReason.KEEPER_UNREACHABLE,
  DisablingReason.SCHEDULE_NON_COMPLIANCE,
  DisablingReason.BEHAVIOR_ISSUE,
  DisablingReason.ROUTE_INCOMPATIBILITY,
  DisablingReason.ADDRESS_INVALID,
  DisablingReason.OTHER,
]

const CLICKABLE_CARRIERS = [CarriersName.COLISSIMO, CarriersName.DHL, CarriersName.GLS, CarriersName.UPS]

function CarrierTooltipContent({ carrier }: { carrier: KeeperCarrier }): ReactElement {
  const isClickable = CLICKABLE_CARRIERS.includes(carrier.carrierName as CarriersName)

  if (carrier.allowed) {
    if (!isClickable) return null
    return <>{`Cliquer pour désactiver ${carrier.carrierName}`}</>
  }

  if (!isClickable) {
    return <>Transporteur non actif dans cette zone</>
  }
  if (carrier.disablingReason) {
    const date = carrier.expiredSince ? new Date(carrier.expiredSince).toLocaleDateString("fr-FR") : null
    return (
      <TooltipContent>
        <TooltipTitle>Keeper désactivé{date ? ` (${date})` : ""}</TooltipTitle>
        <TooltipField>
          <strong>Raison :</strong> {REASON_LABELS[carrier.disablingReason] ?? carrier.disablingReason}
        </TooltipField>
        {carrier.disablingRequestedBy && (
          <TooltipField>
            <strong>Demandé par :</strong>{" "}
            {REQUESTER_LABELS[carrier.disablingRequestedBy] ?? carrier.disablingRequestedBy}
          </TooltipField>
        )}
        {carrier.internalComment && (
          <TooltipField>
            <strong>Commentaire interne :</strong> {carrier.internalComment}
          </TooltipField>
        )}
        <TooltipAction>Cliquer pour l&apos;activer.</TooltipAction>
      </TooltipContent>
    )
  }

  return <TooltipContent>Jamais activé pour ce transporteur. Cliquer pour l&apos;activer.</TooltipContent>
}

function CarrierButton({ carrier, cursor, loading }: KeeperCarrierButtonProps): ReactElement {
  return (
    <ButtonContainer icon cursor={cursor}>
      {
        {
          [CarriersName.COLISSIMO]: <CarrierLogo src={ColissionLogoSolo} size="mini" />,
          [CarriersName.DHL]: <CarrierLogo src={DHLLogo} size="mini" />,
          [CarriersName.GEODIS]: <CarrierLogo src={GeodisLogoSolo} size="mini" />,
          [CarriersName.GLS]: <CarrierLogo src={GLSLogo} size="mini" />,
          [CarriersName.RELAISCOLIS]: <CarrierLogo src={RelaisColisLogo} size="mini" width={"25px"} />,
          [CarriersName.UPS]: <CarrierLogo src={UPSLogo} size="mini" width={"25px"} />,
          [CarriersName.GEEV]: <CarrierLogo src={GeevLogo} size="mini" />,
          [CarriersName.POPPINS]: <CarrierLogo src={PoppinsLogo} size="mini" />,
        }[carrier.carrierName]
      }
      {loading ? (
        <LoaderIconContainer>
          <Loader size="mini" active inverted />
        </LoaderIconContainer>
      ) : carrier.allowed ? (
        <CheckIconContainer>
          <CheckIcon name="check" size="small" />
        </CheckIconContainer>
      ) : (
        <CloseIconContainer>
          <CloseIcon name="close" size="small" />
        </CloseIconContainer>
      )}
    </ButtonContainer>
  )
}

function AllowedCarrier({ carrier, updateAllowedCarrier, loading }: AllowedCarrierProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [requestedBy, setRequestedBy] = useState<{ label: string; value: DisablingRequestedBy }>(null)
  const [reason, setReason] = useState<{ label: string; value: DisablingReason }>(null)
  const [internalComment, setInternalComment] = useState("")

  const requestedByOptions = [
    { label: REQUESTER_LABELS[DisablingRequestedBy.CARRIER], value: DisablingRequestedBy.CARRIER },
    { label: REQUESTER_LABELS[DisablingRequestedBy.PICKME_CARE], value: DisablingRequestedBy.PICKME_CARE },
    { label: REQUESTER_LABELS[DisablingRequestedBy.PICKME_OPS], value: DisablingRequestedBy.PICKME_OPS },
    { label: REQUESTER_LABELS[DisablingRequestedBy.KEEPER], value: DisablingRequestedBy.KEEPER },
  ]

  const reasonOptions = useMemo(() => {
    if (!requestedBy) return []

    let allowedReasons: DisablingReason[] = []

    switch (requestedBy.value) {
      case DisablingRequestedBy.KEEPER:
        allowedReasons = REASONS_FOR_KEEPER
        break
      case DisablingRequestedBy.CARRIER:
        allowedReasons = REASONS_FOR_CARRIER
        break
      case DisablingRequestedBy.PICKME_OPS:
      case DisablingRequestedBy.PICKME_CARE:
        allowedReasons = REASONS_FOR_OPS_CARE
        break
      default:
        allowedReasons = []
    }

    return allowedReasons.map(r => ({
      label: REASON_LABELS[r],
      value: r,
    }))
  }, [requestedBy])

  useEffect(() => {
    setReason(null)
  }, [requestedBy])

  const handleClose = () => {
    setOpen(false)
    setRequestedBy(null)
    setReason(null)
    setInternalComment("")
  }

  const handleConfirm = () => {
    if (carrier.allowed) {
      updateAllowedCarrier({
        disablingRequestedBy: requestedBy.value,
        disablingReason: reason.value,
        internalComment,
      })
    } else {
      updateAllowedCarrier()
    }
    handleClose()
  }

  if (CLICKABLE_CARRIERS.includes(carrier.carrierName as CarriersName)) {
    return (
      <Modal
        basic={!carrier.allowed}
        onClose={handleClose}
        onOpen={() => setOpen(true)}
        open={open}
        size="small"
        trigger={
          <Popup
            content={<CarrierTooltipContent carrier={carrier} />}
            size="small"
            position="bottom left"
            trigger={
              <PopupMessage onClick={() => setOpen(true)}>
                <CarrierButton carrier={carrier} cursor loading={loading} />
              </PopupMessage>
            }
          />
        }
      >
        {carrier.allowed ? (
          <Modal.Header>Vous êtes sur le point de désactiver {carrier.carrierName} pour ce Keeper?</Modal.Header>
        ) : (
          <Header icon>
            <Icon name="attention" />
            En êtes-vous vraiment sûr.e ?
          </Header>
        )}

        <Modal.Content style={!carrier.allowed ? { display: "flex", justifyContent: "center" } : undefined}>
          {carrier.allowed ? (
            <>
              <DisablingSectionsContainer>
                <DisablingSection>
                  <DisablingLabel>Demandé par</DisablingLabel>
                  <Select
                    value={requestedBy}
                    onChange={setRequestedBy}
                    options={requestedByOptions}
                    placeholder=""
                    required
                  />
                </DisablingSection>
                <DisablingSection>
                  <DisablingLabel>Raison</DisablingLabel>
                  <Select
                    value={reason}
                    onChange={setReason}
                    options={reasonOptions}
                    isDisabled={!requestedBy}
                    placeholder=""
                    required
                  />
                </DisablingSection>
              </DisablingSectionsContainer>
              <DisablingLabel>Commentaire interne (optionnel)</DisablingLabel>
              <Input fluid value={internalComment} onChange={e => setInternalComment(e.target.value)} placeholder="" />
            </>
          ) : (
            `Vous êtes sur le point d'activer ${carrier.carrierName} pour ce Keeper`
          )}
        </Modal.Content>

        <Modal.Actions>
          {carrier.allowed ? (
            <>
              <Button color="black" onClick={handleClose}>
                Annuler
              </Button>
              <Button positive onClick={handleConfirm} disabled={!requestedBy || !reason}>
                <Icon name="checkmark" /> Valider
              </Button>
            </>
          ) : (
            <>
              <Button basic color="red" inverted onClick={() => setOpen(false)}>
                <Icon name="remove" /> Non !
              </Button>
              <Button color="green" inverted onClick={handleConfirm}>
                <Icon name="checkmark" /> Confirmer
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal>
    )
  }

  const tooltipContent = <CarrierTooltipContent carrier={carrier} />
  if (!carrier.allowed && tooltipContent) {
    return (
      <Popup
        content={tooltipContent}
        size="small"
        position="bottom left"
        trigger={
          <PopupMessage>
            <CarrierButton carrier={carrier} loading={loading} />
          </PopupMessage>
        }
      />
    )
  }

  return <CarrierButton carrier={carrier} loading={loading} />
}

export default AllowedCarrier
